import React, { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { DateRangePicker } from "react-date-range";
import { defaultStaticRanges } from "react-date-range/dist/defaultRanges";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// Labels from react-date-range's built-in presets that rely on a future
// boundary. These are dropped so nothing lets the user pick a future date.
const FUTURE_LEANING_LABELS = ["Next Week", "Next Month"];

const pastOnlyStaticRanges = defaultStaticRanges.filter(
  (range) => !FUTURE_LEANING_LABELS.includes(range.label),
);

/**
 * Same shape/usage as the common <Daterangepicker />, except:
 *  - future dates are disabled (maxDate = today)
 *  - only past/present common ranges are offered (Today, Yesterday,
 *    This Week, Last Week, This Month, Last Month)
 *  - a tooltip is always visible on the panel explaining that filtering
 *    happens on the "created on" date
 */
const ExportDateRangePicker = ({
  selectionRange,
  setSelectionRange,
  style,
  renderInput,
}) => {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    let adjustedEnd = endDate ? new Date(endDate) : null;
    if (adjustedEnd) {
      if (adjustedEnd.getTime() > today.getTime()) adjustedEnd = new Date(today);
      adjustedEnd.setHours(23, 59, 59, 999);
    }

    let adjustedStart = startDate ? new Date(startDate) : null;
    if (adjustedStart && adjustedStart.getTime() > today.getTime()) {
      adjustedStart = new Date(today);
    }

    setSelectionRange({
      ...selectionRange,
      startDate: adjustedStart,
      endDate: adjustedEnd || adjustedStart,
    });

    if (
      startDate &&
      endDate &&
      (startDate.getTime() !== endDate.getTime() || ranges.selection.label)
    ) {
      setOpen(false);
    }
  };

  const defaultInput = (
    <input
      type="text"
      readOnly
      value={`${selectionRange.startDate.toLocaleDateString()} - ${selectionRange.endDate.toLocaleDateString()}`}
      onClick={() => setOpen(!open)}
      style={{
        display: "block",
        boxSizing: "border-box",
        padding: "8px",
        width: "100%",
        height: "44px",
        cursor: "pointer",
        backgroundColor: "#d4dfed",
        border: "2px solid #ccc",
        borderRadius: "8px",
      }}
    />
  );

  return (
    <div
      ref={pickerRef}
      style={{ position: "relative", display: "block", width: "100%", boxSizing: "border-box" }}
    >
      {renderInput
        ? renderInput({
            open,
            setOpen,
            selectionRange,
            onClick: () => setOpen(!open),
          })
        : defaultInput}

      {open &&
        ReactDOM.createPortal(
          <>
            <div
              onClick={() => setOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1400,
                backgroundColor: "rgba(0,0,0,0.4)",
              }}
            />
            <div
              className="shadow-lg border-2 rounded-lg overflow-hidden"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1401,
                maxHeight: "90vh",
                maxWidth: "90vw",
                width: "max-content",
              }}
            >
              <DateRangePicker
                inputRanges={[]}
                staticRanges={pastOnlyStaticRanges}
                ranges={[selectionRange]}
                onChange={handleSelect}
                maxDate={today}
              />
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default ExportDateRangePicker;